import * as React from "react"
import { FieldError, useFormContext } from "react-hook-form"
import Select, { MultiValue, StylesConfig } from "react-select"
import { Box, BoxProps, FormControl, useTheme, WithCSSVar } from "@chakra-ui/react"

import { InputError } from "./InputError"
import { InputLabel } from "./InputLabel"

type Option = { label: string; value: any }

interface Props extends BoxProps {
  name: string
  options: Record<string, string | number>
  placeholder?: string
  label?: string
  subLabel?: string
  helpText?: string
  isRequired?: boolean
  isDisabled?: boolean
}

export const MultiSelect = ({
  label,
  subLabel,
  helpText,
  placeholder,
  isRequired,
  isDisabled,
  options,
  ...props
}: Props) => {
  const {
    register,
    unregister,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()

  const theme = useTheme()
  const fieldError = errors?.[props.name] as FieldError | string

  React.useEffect(() => {
    register?.(props.name)
    return () => unregister(props.name)
  }, [props.name, register, unregister])

  const handleMultiChange = (val: MultiValue<Option>) => {
    const values = val && val.length > 0 ? val : []
    setValue(props.name, values ? values.map((v) => v.value) : [], { shouldDirty: true })
  }

  const value = watch(props.name) as string[] | null
  const finalValue = value?.map((val) => ({ value: val, label: options[val] }))

  const selectOptions = React.useMemo(
    () => Object.entries(options).map(([val, label]) => ({ value: val, label })),
    [options],
  )
  return (
    <FormControl isInvalid={!!fieldError} isRequired={isRequired}>
      <InputLabel label={label} subLabel={subLabel} helpText={helpText} name={props.name} />
      <Box w="100%" {...props}>
        <Select
          // required={isRequired}
          inputId={props.name}
          aria-label={label ? `${label} multi select` : undefined}
          isDisabled={isDisabled}
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
          onChange={handleMultiChange}
          isMulti
          placeholder={placeholder || ""}
          options={selectOptions}
          styles={customStyle(theme, !!fieldError)}
          value={finalValue}
        />
      </Box>
      <InputError error={fieldError} />
    </FormControl>
  )
}

interface ControlledProps extends Omit<Props, "onChange" | "name"> {
  value: MultiValue<Option>
  onChange: (val: MultiValue<Option>) => void
}

export const ControlledMultiSelect = ({
  label,
  subLabel,
  value,
  options,
  onChange,
  placeholder,
  isDisabled,
  ...props
}: ControlledProps) => {
  const theme = useTheme()

  const handleMultiChange = (val: MultiValue<Option>) => {
    const values = val && val.length > 0 ? val : []
    onChange(values)
  }

  const selectOptions = React.useMemo(
    () => Object.entries(options).map(([val, label]) => ({ value: val, label })),
    [options],
  )
  return (
    <Box w="100%" {...props}>
      <Select
        // required={props.isRequired}
        isDisabled={isDisabled}
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        onChange={handleMultiChange}
        isMulti
        placeholder={placeholder || ""}
        options={selectOptions}
        styles={customStyle(theme)}
        value={value}
        aria-label={`${label} multi select`}
      />
    </Box>
  )
}

const customStyle = (theme: WithCSSVar<Record<any, any>>, hasError?: boolean): StylesConfig<any, any> => ({
  menu: (styles) => ({
    ...styles,
    zIndex: 999,
    backgroundColor: "#2B2B2B",
  }),
  placeholder: (styles) => ({
    ...styles,
    overflow: "hidden",
    whiteSpace: "nowrap",
    color: theme.colors.gray[900],
  }),
  option: (styles, state) => ({
    ...styles,
    textAlign: "left",
    backgroundColor: state.isFocused ? theme.colors.gray[800] : "transparent",
    color: "white",
    ":hover": {
      backgroundColor: theme.colors.gray[800],
    },
  }),

  control: (styles, state) => ({
    ...styles,
    paddingTop: 2,
    paddingLeft: 8,
    paddingBottom: 2,
    borderRadius: 2,
    borderWidth: 0,
    width: "100%",
    boxShadow: hasError
      ? `inset 0px 0px 0px 2px ${theme.colors.red[500]}`
      : state.isFocused
      ? `inset 0px 0px 0px 2px ${theme.colors.pink[500]}`
      : "none",
    backgroundColor: "#2B2B2B",
    ":hover": {
      backgroundColor: "#303030",
      boxShadow: state.isFocused ? `inset 0px 0px 0px 2px ${theme.colors.pink[500]}` : "none",
    },
  }),
  clearIndicator: (styles) => ({
    ...styles,
    color: theme.colors.gray[400],
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: theme.colors.gray[500],
    strokeWidth: "1px",
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    backgroundColor: theme.colors.gray[600],
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "white",
    backgroundColor: theme.colors.gray[600],
    borderRadius: 0,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "white",
    borderRadius: 0,
    backgroundColor: theme.colors.gray[600],
  }),
})
